import React, { useEffect, useRef, useState } from "react";
import { SignalBars } from "./common";
import { Lock, Unlock, KeyRound, ShieldCheck, Check } from "lucide-react";

const STEPS = [
  { t: "INITIALISATION DU NODE 7781-A", tag: "BOOT" },
  { t: "MONTAGE DU SYSTÈME DE FICHIERS CHIFFRÉ", tag: "FS" },
  { t: "SAISIE AUTOMATIQUE DES CLÉS D'ACCÈS", tag: "AUTOFILL" },
  { t: "NÉGOCIATION HANDSHAKE TLS 1.3", tag: "FW" },
  { t: "DÉVERROUILLAGE PROTOCOLE ICE-9", tag: "UNLOCK" },
  { t: "ÉCHANGE DE CLÉS RSA-4096", tag: "KEY" },
  { t: "DÉCHIFFREMENT DU SAS DE SÉCURITÉ", tag: "DECRYPT" },
  { t: "OUVERTURE DU SAS BLINDÉ · NIVEAU 3", tag: "UNLOCK" },
  { t: "CONTOURNEMENT PARE-FEU TACHYONIQUE", tag: "FW" },
  { t: "ROUTAGE OIGNON · 7 RELAIS CHIFFRÉS", tag: "ROUTE" },
  { t: "BROUILLAGE DE L'EMPREINTE RÉSEAU", tag: "ROUTE" },
  { t: "VÉRIFICATION BIOMÉTRIQUE DU PORTEUR", tag: "BIO" },
  { t: "SCELLEMENT DU TUNNEL QUANTIQUE", tag: "KEY" },
  { t: "SYNCHRONISATION DU TERMINAL RÉPLA", tag: "SYNC" },
];

const HEX = "0123456789ABCDEF";
const rndHex = (n) => Array.from({ length: n }, () => HEX[Math.floor(Math.random() * 16)]).join("");

function ScrambleKey() {
  const [s, setS] = useState(rndHex(24));
  useEffect(() => {
    const id = setInterval(() => setS(rndHex(24)), 70);
    return () => clearInterval(id);
  }, []);
  return <span>{s.replace(/(.{4})/g, "$1 ").trim()}</span>;
}

function BinaryRain() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    let w = (c.width = window.innerWidth);
    let h = (c.height = window.innerHeight);
    const fs = 14;
    let drops = Array(Math.floor(w / fs)).fill(0).map(() => Math.random() * -60);
    const prim = getComputedStyle(document.documentElement).getPropertyValue("--r-primary").trim() || "#2DD4BF";
    let raf;
    const draw = () => {
      ctx.fillStyle = "rgba(7,9,13,0.16)";
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = prim;
      ctx.font = `${fs}px monospace`;
      for (let i = 0; i < drops.length; i++) {
        ctx.globalAlpha = Math.random() * 0.5 + 0.35;
        ctx.fillText(Math.random() > 0.5 ? "1" : "0", i * fs, drops[i] * fs);
        if (drops[i] * fs > h && Math.random() > 0.975) drops[i] = 0;
        drops[i]++;
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    draw();
    const onR = () => {
      w = c.width = window.innerWidth;
      h = c.height = window.innerHeight;
      drops = Array(Math.floor(w / fs)).fill(0).map(() => Math.random() * -60);
    };
    window.addEventListener("resize", onR);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onR);
    };
  }, []);
  return <canvas ref={ref} className="absolute inset-0" style={{ opacity: 0.28 }} />;
}

const TAG_ICON = { UNLOCK: Unlock, KEY: KeyRound, DECRYPT: KeyRound, BIO: ShieldCheck };

export default function DarkNetSequence({ identifier, role, onComplete }) {
  const [n, setN] = useState(0);
  const [dots, setDots] = useState(0);
  const done = n >= STEPS.length;

  useEffect(() => {
    const id = setInterval(() => setDots((d) => (d + 1) % 11), 90);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (done) return;
    const t = setTimeout(() => setN((x) => x + 1), n === 1 ? 560 : 300);
    return () => clearTimeout(t);
  }, [n, done]);

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center overflow-hidden" style={{ background: "var(--r-void)" }}>
      <BinaryRain />
      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 42%, rgba(45,212,191,0.07), rgba(7,9,13,0.92) 72%)" }} />

      <div className="relative z-10 w-full px-6" style={{ maxWidth: 560 }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <SignalBars />
            <span className="font-mono text-[10px] tracking-[0.3em]" style={{ color: "var(--r-primary)" }}>RÉPLA // SECURE SHELL</span>
          </div>
          <span className="font-mono text-[10px] tracking-[0.25em] blink" style={{ color: "var(--r-text-muted)" }}>● LIVE</span>
        </div>

        <div className="frame p-5" style={{ background: "rgba(10,13,18,0.82)", backdropFilter: "blur(6px)" }}>
          <div className="flex items-center justify-between mb-3 pb-3" style={{ borderBottom: "1px solid color-mix(in srgb, var(--r-primary) 22%, transparent)" }}>
            <div className="min-w-0">
              <div className="sys-label">PORTEUR AUTHENTIFIÉ</div>
              <div className="font-mono text-sm truncate" style={{ color: "var(--r-text)" }}>{identifier || "—"}</div>
            </div>
            <div className="font-mono text-[9px] tracking-[0.2em] px-2 py-1" style={{ border: "1px solid color-mix(in srgb, var(--r-primary) 40%, transparent)", color: "var(--r-primary)" }}>
              {role === "gm" ? "CONSOLE MJ" : "TERMINAL JOUEUR"}
            </div>
          </div>

          <div className="font-mono text-[11px] leading-relaxed space-y-1" style={{ minHeight: 232 }}>
            {STEPS.map((s, i) => {
              if (i > n) return null;
              const isCurrent = i === n && !done;
              const ok = i < n || done;
              const Icon = TAG_ICON[s.tag] || Check;
              return (
                <div key={i} className="flex items-center gap-2 fade-up" data-testid={`darknet-step-${i}`}>
                  <span style={{ color: "var(--r-primary)" }}>›</span>
                  <span style={{ color: ok ? "var(--r-text)" : "var(--r-text-muted)", flex: 1 }}>
                    {s.t}
                    {s.tag === "AUTOFILL" && isCurrent && (
                      <span style={{ color: "var(--r-primary)" }}> {"●".repeat(dots)}{"·".repeat(10 - dots)}</span>
                    )}
                    {(s.tag === "KEY" || s.tag === "DECRYPT") && isCurrent && (
                      <span className="ml-1" style={{ color: "color-mix(in srgb, var(--r-primary) 70%, transparent)" }}><ScrambleKey /></span>
                    )}
                  </span>
                  {ok ? (
                    <span className="flex items-center gap-1" style={{ color: "#22C55E" }}><Icon size={12} /> OK</span>
                  ) : (
                    <span className="blink" style={{ color: "var(--r-primary)" }}>…</span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-4 h-[3px] overflow-hidden" style={{ background: "color-mix(in srgb, var(--r-primary) 18%, transparent)" }}>
            <div style={{ height: "100%", width: `${Math.min(100, (n / STEPS.length) * 100)}%`, background: "var(--r-primary)", boxShadow: "0 0 10px var(--r-primary)", transition: "width 0.3s ease" }} />
          </div>

          <div className="mt-4">
            {done ? (
              <div className="fade-up">
                <div className="flex items-center justify-center gap-2 font-mono text-sm tracking-[0.3em] mb-3 text-glow" style={{ color: "#22C55E" }}>
                  <Unlock size={16} /> <span>ACCÈS AUTORISÉ</span>
                </div>
                <button data-testid="unlock-terminal" onClick={onComplete} className="btn btn-solid w-full flex items-center justify-center gap-2 tap" style={{ boxShadow: "0 0 18px color-mix(in srgb, var(--r-primary) 55%, transparent)" }}>
                  <Unlock size={16} /> DÉVERROUILLER LE TERMINAL
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 font-mono text-sm tracking-[0.3em]" style={{ color: "var(--r-text-muted)" }}>
                <Lock size={16} /> <span>DÉCHIFFREMENT EN COURS</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
